import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import quoteService from '../../../services/quote.service';
import type {
  CustomerCreatePayload,
  CustomerCreateResult,
  CustomerFormOptions,
  CustomerLookupRow,
} from '../fixtures';

import Row from '../../pro/components/orderform/Row';
import { Label, LabelStacked } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput from '../../pro/components/orderform/SelectInput';
import FlagInput, { type FlagValue } from '../../pro/components/orderform/FlagInput';
import Stack from '../../pro/components/orderform/Stack';
import Card from './Card';
import { createErrorMessage } from './quoteErrors';

interface Props {
  /** Pre-fills the Name field (the wizard's typed search text). */
  initialName: string;
  onClose: () => void;
  onCreated: (result: CustomerCreateResult) => void;
}

const LBL = 140;

/** V1 editcustomers new-record defaults (tablemaint.pl:13731-13741) — must
 *  match the backend CustomerCreateIn defaults exactly. */
function initialForm(initialName: string): CustomerCreatePayload {
  return {
    custname: initialName.trim().toUpperCase(),
    marketcode: 0,
    custadd: '', custadd2: '', custcity: '', custstate: '', custzip: '',
    custphone: '', custfax: '', communication: '',
    keyaccount: 'AVERAGE', salesrep: '', logisticscoordinator: '',
    brokeragecsr: '', custtype: 'MASTER', masterrecno: 0,
    salesforceid: '', custregion: '', allowbrokerage: 'Y',
    showcustomerco: 'N', customlfd: 'N', sealverification: 'N',
    hidecustomerlabel: 'N', custombl: 'N',
    showrcvbol: 'Y', showrcvequipment: 'Y', showrcvpo: 'Y', showrcvseal: 'Y',
    showlotnum: 'Y', showserialnum: 'Y', showexpiration: 'Y', showmanufacture: 'Y',
    additionalinfo: '', shippinginstructions: '',
  };
}

const Divider: React.FC = () => <div className="h-px bg-slate-100 dark:bg-slate-700 my-2" />;

/**
 * Full V1-parity "New Customer" form (tablemaint.pl editcustomers, new-record
 * path) for the create wizard's "Add As New Customer". Creates a real
 * cms.customers row and hands the new recno back via onCreated.
 *
 * Excluded on purpose (V1 disables these until after the first save):
 * Contacts, Current SOP, Product Codes, Whse Cust, Created/Last Used.
 * Master Customer appears only for custtype=DIVISION (masters-only lookup).
 *
 * Self-portals to <body> at z-[120] so it layers above the quote drawer (a body
 * portal at z-[100]) it's launched from.
 */
const NewCustomerModal: React.FC<Props> = ({ initialName, onClose, onCreated }) => {
  const [form, setForm] = useState<CustomerCreatePayload>(() => initialForm(initialName));
  const [options, setOptions] = useState<CustomerFormOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Master-customer picker (DIVISION only).
  const [masterQuery, setMasterQuery] = useState('');
  const [masterResults, setMasterResults] = useState<CustomerLookupRow[]>([]);
  const [masterPicked, setMasterPicked] = useState<CustomerLookupRow | null>(null);
  const [masterOpen, setMasterOpen] = useState(false);

  const patch = (p: Partial<CustomerCreatePayload>) => setForm((f) => ({ ...f, ...p }));

  // Dropdown sources — one call.
  useEffect(() => {
    let cancelled = false;
    quoteService
      .customerFormOptions()
      .then((o) => { if (!cancelled) setOptions(o); })
      .catch(() => { if (!cancelled) setError('Could not load the customer form options.'); });
    return () => { cancelled = true; };
  }, []);

  // Esc closes this modal only. Capture-phase + stopPropagation so the
  // wizard's own (bubble-phase) Esc handler doesn't also fire its
  // discard-draft confirm underneath.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced masters-only lookup for the DIVISION master picker.
  useEffect(() => {
    const q = masterQuery.trim();
    if (q.length < 2 || (masterPicked && q === masterPicked.custname)) {
      setMasterResults([]);
      setMasterOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const rows = await quoteService.lookupCustomer(q, true);
        setMasterResults(rows);
        setMasterOpen(true);
      } catch {
        setMasterResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterQuery]);

  const pickMaster = (row: CustomerLookupRow) => {
    setMasterPicked(row);
    setMasterOpen(false);
    setMasterQuery(row.custname);
    patch({ masterrecno: row.customercode });
  };

  const valid =
    form.custname.trim().length > 0 &&
    form.marketcode > 0 &&
    (form.custtype !== 'DIVISION' || (form.masterrecno ?? 0) > 0);

  async function handleSave() {
    if (saving || !valid) return;
    setSaving(true);
    setError(null);
    try {
      const result = await quoteService.createCustomer(form);
      onCreated(result);
    } catch (e) {
      setError(createErrorMessage(e, 'Failed to create customer. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  const flag = (field: keyof CustomerCreatePayload, label: string) => (
    <FlagInput
      label={label}
      value={(form[field] ?? 'N') as FlagValue}
      onChange={(v) => patch({ [field]: v })}
      labelWidth={150}
    />
  );

  // Rendered only while open. Alt+C closes; creating the customer goes through a
  // native form submit, so Enter already covers it.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });

  // Rendered only while open (parent unmounts on close) — unmount resets the drag.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-[760px] max-h-[85vh] overflow-y-auto bg-white border border-slate-200 rounded-[10px] shadow-2xl dark:bg-slate-800 dark:border-slate-700"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-customer-modal-title"
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <ModalHeader
          title={
            <span className="inline-flex items-center gap-2">
              <UserPlusIcon className="h-4 w-4 text-blue-300" />
              New Customer
            </span>
          }
          titleId="new-customer-modal-title"
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="sticky top-0 z-10 rounded-t-[10px]"
        />

        <div className="px-4 py-3 flex flex-col gap-3">
          <Card icon={<UserPlusIcon className="h-3.5 w-3.5" />} title="Customer">
            <Row>
              <Label width={LBL} highlight="red">Name</Label>
              <TextInput
                value={form.custname}
                onChange={(v) => patch({ custname: v.toUpperCase() })}
                ariaLabel="Name"
                placeholder="Customer name"
              />
            </Row>
            <Row>
              <Label width={LBL}>Address</Label>
              <TextInput value={form.custadd ?? ''} onChange={(v) => patch({ custadd: v.toUpperCase() })} ariaLabel="Address" placeholder="—" />
            </Row>
            <Row>
              <Label width={LBL}>Address 2</Label>
              <TextInput value={form.custadd2 ?? ''} onChange={(v) => patch({ custadd2: v.toUpperCase() })} ariaLabel="Address 2" placeholder="—" />
            </Row>
            <Row>
              <Label width={LBL}>City / State / Zip</Label>
              <TextInput value={form.custcity ?? ''} onChange={(v) => patch({ custcity: v.toUpperCase() })} ariaLabel="City" placeholder="City" />
              <TextInput value={form.custstate ?? ''} onChange={(v) => patch({ custstate: v.toUpperCase() })} ariaLabel="State" width={48} placeholder="ST" />
              <TextInput value={form.custzip ?? ''} onChange={(v) => patch({ custzip: v })} ariaLabel="Zip" width={96} placeholder="ZIP" />
            </Row>
            <Row>
              <Label width={LBL}>Phone / Fax</Label>
              <TextInput value={form.custphone ?? ''} onChange={(v) => patch({ custphone: v })} ariaLabel="Phone" width={140} placeholder="—" />
              <TextInput value={form.custfax ?? ''} onChange={(v) => patch({ custfax: v })} ariaLabel="Fax" width={140} placeholder="—" />
            </Row>
            <Row>
              <Label width={LBL}>Pref Commun</Label>
              <SelectInput
                value={form.communication ?? ''}
                onChange={(v) => patch({ communication: v })}
                options={options?.communication ?? ['']}
                width={120}
                ariaLabel="Pref Commun"
                placeholder="—"
              />
            </Row>

            <Divider />

            <Row>
              <Label width={LBL}>Key Account</Label>
              <SelectInput
                value={form.keyaccount ?? 'AVERAGE'}
                onChange={(v) => patch({ keyaccount: v })}
                options={options?.keyAccounts ?? ['AVERAGE']}
                width={180}
                ariaLabel="Key Account"
              />
            </Row>
            <Row>
              <Label width={LBL}>Sales Rep</Label>
              <SelectInput
                value={form.salesrep ?? ''}
                onChange={(v) => patch({ salesrep: v })}
                options={[{ value: '', label: '—' }, ...(options?.salesReps ?? []).map((r) => ({ value: r.username, label: r.displayname }))]}
                ariaLabel="Sales Rep"
              />
            </Row>
            <Row>
              <Label width={LBL}>LC</Label>
              <SelectInput
                value={form.logisticscoordinator ?? ''}
                onChange={(v) => patch({ logisticscoordinator: v })}
                options={[{ value: '', label: '—' }, ...(options?.csrs ?? []).map((r) => ({ value: r.username, label: r.displayname }))]}
                ariaLabel="LC"
              />
            </Row>
            <Row>
              <Label width={LBL} highlight="red">Customer Type</Label>
              <SelectInput
                value={form.custtype ?? 'MASTER'}
                onChange={(v) => {
                  patch({ custtype: v as 'MASTER' | 'DIVISION', masterrecno: 0 });
                  setMasterPicked(null);
                  setMasterQuery('');
                }}
                options={['MASTER', 'DIVISION']}
                width={120}
                ariaLabel="Customer Type"
              />
              <Label highlight="red">Market</Label>
              <SelectInput
                value={form.marketcode ? String(form.marketcode) : ''}
                onChange={(v) => patch({ marketcode: Number(v) || 0 })}
                options={[{ value: '', label: '—' }, ...(options?.markets ?? []).map((m) => ({ value: String(m.recno), label: m.market }))]}
                ariaLabel="Market"
              />
            </Row>

            {/* Master customer — DIVISION only (V1 unlocks the lookup there). */}
            {form.custtype === 'DIVISION' && (
              <Stack>
                <LabelStacked highlight="red">Master Customer (search)</LabelStacked>
                <div className="relative">
                  <TextInput
                    value={masterQuery}
                    onChange={(v) => {
                      setMasterQuery(v);
                      if (masterPicked) {
                        setMasterPicked(null);
                        patch({ masterrecno: 0 });
                      }
                    }}
                    placeholder="Search master customers — min 2 characters…"
                  />
                  {masterOpen && masterResults.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg dark:bg-slate-800 dark:border-slate-700">
                      {masterResults.map((row) => (
                        <button
                          key={row.customercode}
                          type="button"
                          onClick={() => pickMaster(row)}
                          className="w-full text-left px-2.5 py-1.5 text-[0.75rem] hover:bg-blue-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                        >
                          <div className="font-semibold text-slate-800 dark:text-slate-100">{row.custname}</div>
                          <div className="text-[0.6875rem] text-slate-500 dark:text-slate-400">
                            {[row.city, row.state].filter(Boolean).join(', ')}
                            <span className="ml-2 text-slate-400 dark:text-slate-500">#{row.customercode}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Stack>
            )}

            <Row>
              <Label width={LBL}>Salesforce ID</Label>
              <TextInput value={form.salesforceid ?? ''} onChange={(v) => patch({ salesforceid: v })} ariaLabel="Salesforce ID" width={180} placeholder="—" />
              <Label>Region</Label>
              <SelectInput
                value={form.custregion ?? ''}
                onChange={(v) => patch({ custregion: v })}
                options={options?.regions ?? ['']}
                width={140}
                ariaLabel="Region"
                placeholder="—"
              />
            </Row>
            <Row>
              <Label width={LBL}>Allow Brokerage</Label>
              <SelectInput
                value={form.allowbrokerage ?? 'Y'}
                onChange={(v) => patch({ allowbrokerage: v })}
                options={['Y', 'N']}
                width={56}
                ariaLabel="Allow Brokerage"
              />
              <Label>Broker</Label>
              <SelectInput
                value={form.brokeragecsr ?? ''}
                onChange={(v) => patch({ brokeragecsr: v })}
                options={[{ value: '', label: '—' }, ...(options?.csrs ?? []).map((r) => ({ value: r.username, label: r.displayname }))]}
                ariaLabel="Broker"
              />
            </Row>
            <Row>
              <Label width={LBL}>Complexity Score</Label>
              <TextInput
                value={form.complexityscore ? String(form.complexityscore) : ''}
                onChange={(v) => patch({ complexityscore: Number(v) || 0 })}
                ariaLabel="Complexity Score"
                width={96}
                placeholder="—"
              />
            </Row>
          </Card>

          <Card icon={<UserPlusIcon className="h-3.5 w-3.5" />} title="Customer Options">
            <div className="grid grid-cols-2 gap-x-1.5">
              {flag('showcustomerco', 'Show Customer C/O')}
              {flag('customlfd', 'Custom LFD')}
              {flag('sealverification', 'Seal Verification')}
              {flag('hidecustomerlabel', 'Hide Cust on Label')}
            </div>
          </Card>

          <Card icon={<UserPlusIcon className="h-3.5 w-3.5" />} title="Bill of Lading Options">
            <div className="grid grid-cols-2 gap-x-1.5">
              {flag('custombl', 'Custom B/L')}
              {flag('showlotnum', 'Show Lot#')}
              {flag('showrcvbol', 'Show RCV BOL')}
              {flag('showserialnum', 'Show Serial#')}
              {flag('showrcvequipment', 'Show RCV Equip#')}
              {flag('showexpiration', 'Show Expiration')}
              {flag('showrcvpo', 'Show RCV PO')}
              {flag('showmanufacture', 'Show Manufacture')}
              {flag('showrcvseal', 'Show RCV Seal')}
            </div>
          </Card>

          <div className="flex gap-3">
            <Stack className="flex-1">
              <LabelStacked>Additional Info</LabelStacked>
              <textarea
                value={form.additionalinfo ?? ''}
                onChange={(e) => patch({ additionalinfo: e.target.value })}
                className="w-full min-h-[64px] border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-[0.75rem] text-slate-900 dark:text-gray-100 bg-white dark:bg-gray-900 resize-y font-sans"
              />
            </Stack>
            <Stack className="flex-1">
              <LabelStacked>Shipping Instructions</LabelStacked>
              <textarea
                value={form.shippinginstructions ?? ''}
                onChange={(e) => patch({ shippinginstructions: e.target.value })}
                className="w-full min-h-[64px] border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-[0.75rem] text-slate-900 dark:text-gray-100 bg-white dark:bg-gray-900 resize-y font-sans"
              />
            </Stack>
          </div>
        </div>

        {/* ── Error banner ─────────────────────────────────────────────── */}
        {error && (
          <div className="mx-4 mb-2 px-3 py-2 rounded-md border border-red-200 bg-red-50 text-[0.75rem] text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="sticky bottom-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!valid || saving}
            className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded-md cursor-pointer hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NewCustomerModal;
